import { TickSize } from '@eih-netbroker/api-instrument';
import BigNumber from 'bignumber.js';
import { useMemo } from 'react';
import { InstrumentTickSizeMap } from '../../state';
import {
  ExpiryType,
  InstrumentStoreItem,
  InventoryManagementMethod,
  ListedInstrument,
  OrderType,
  TradeType,
  TradingSession,
} from '../../types';
import { isDefined } from '../../utils';
import { GetInstrumentTickSizeOfList } from '../instrument';
import { orderTypesWhenStopActivationPriceRequired } from './orderConst';

interface OrderFormData {
  instrument?: ListedInstrument | null;
  orderType?: OrderType;
  tradeType?: TradeType;
  quantity?: number;
  price?: number;
  priceRaw?: string;
  stopActivationPrice?: number;
  stopActivationPriceRaw?: string;
  expiryType?: ExpiryType;
  expiry?: Date;
  inventoryManagementMethod?: InventoryManagementMethod | null;
  tradingSession?: TradingSession;
}

type UseDefaultOrderPricesProps = {
  orderForm: OrderFormData;
  instrumentStoreItem: InstrumentStoreItem | undefined;
  instrumentTickSize: InstrumentTickSizeMap;
  getInstrumentTickSizeOfList: GetInstrumentTickSizeOfList;
};

type HandleTickSizeOperation = 'addition' | 'subtraction';

// Specs for how the default limit/activation prices are computed:
// https://ersteinv.atlassian.net/wiki/spaces/Projects/pages/39554270/R+szletes+k+tjegy+widget+adatfelt+lt+s
export function useDefaultOrderPricesCommon({
  orderForm,
  instrumentStoreItem,
  instrumentTickSize,
  getInstrumentTickSizeOfList,
}: UseDefaultOrderPricesProps) {
  const { instrument, orderType, tradeType } = orderForm;
  const price = instrumentStoreItem?.price?.value;
  const tickSizesForInstrument = instrumentTickSize[instrument?.ric ?? ''];

  const tickSize = useMemo(() => {
    return isDefined(tickSizesForInstrument) && isDefined(price)
      ? getInstrumentTickSizeOfList(tickSizesForInstrument, price)
      : undefined;
  }, [getInstrumentTickSizeOfList, price, tickSizesForInstrument]);

  const defaultActivationPrice = useMemo((): number | undefined => {
    if (!isDefined(price) || !isDefined(orderType)) {
      return undefined;
    }
    if (!isDefined(tickSize)) {
      return price;
    }

    if (orderTypesWhenStopActivationPriceRequired.includes(orderType)) {
      if (tradeType === 'buy') {
        return handleTickSizeShift(
          adjustForTickSize(price, tickSize),
          price,
          tickSizesForInstrument,
          getInstrumentTickSizeOfList,
          1,
          'addition',
        );
      } else {
        return handleTickSizeShift(
          adjustForTickSize(price, tickSize),
          price,
          tickSizesForInstrument,
          getInstrumentTickSizeOfList,
          1,
          'subtraction',
        );
      }
    }

    return undefined;
  }, [orderType, price, tickSize, tradeType, tickSizesForInstrument, getInstrumentTickSizeOfList]);

  const defaultLimitPrice = useMemo((): number | undefined => {
    if (!isDefined(price) || !isDefined(orderType)) {
      return undefined;
    }
    if (!isDefined(tickSize)) {
      return price;
    }

    if (orderType === OrderType.Limit || orderType === OrderType.Oco) {
      if (tradeType === 'buy') {
        return handleTickSizeShift(
          adjustForTickSize(price, tickSize),
          price,
          tickSizesForInstrument,
          getInstrumentTickSizeOfList,
          1,
          'subtraction',
        );
      } else {
        return handleTickSizeShift(
          adjustForTickSize(price, tickSize),
          price,
          tickSizesForInstrument,
          getInstrumentTickSizeOfList,
          1,
          'addition',
        );
      }
    }

    if (orderType === OrderType.StopLimit) {
      if (tradeType === 'buy') {
        return handleTickSizeShift(
          adjustForTickSize(price, tickSize),
          price,
          tickSizesForInstrument,
          getInstrumentTickSizeOfList,
          2,
          'addition',
        );
      } else {
        return handleTickSizeShift(
          adjustForTickSize(price, tickSize),
          price,
          tickSizesForInstrument,
          getInstrumentTickSizeOfList,
          2,
          'subtraction',
        );
      }
    }

    return undefined;
  }, [tickSize, orderType, tradeType, price, tickSizesForInstrument, getInstrumentTickSizeOfList]);

  return { defaultLimitPrice, defaultActivationPrice, tickSize };
}

/** Handle price adjustment according to tick size. Explicitly rounds down to the nearest tick size. */
const adjustForTickSize = (price: number, tickSize: number): BigNumber => {
  const priceN = new BigNumber(price);
  const remainderN = priceN.mod(new BigNumber(tickSize)).abs();

  // If price is not divisible by tickSize then substract the remainder - otherwise price is ok.
  return remainderN.gt(0) ? priceN.minus(remainderN) : priceN;
};

/** Finds the appropriate tick size to subtract from the price. Handles edge cases where the price is at the lower limit of a tick size. */
const handleTickSizeShift = (
  price: BigNumber,
  nonAdjustedPrice: number,
  // tickSize: number,
  tickSizesForInstrument: Array<TickSize>,
  getInstrumentTickSizeOfList: (tickSizes: Array<TickSize>, price: number) => number,
  numberOfShifts: number = 1,
  operation: HandleTickSizeOperation,
): number => {
  // If there are no tick sizes for the instrument or the price is already less than the non-adjusted instrumentStoreItem price
  // (and needs only 1 shift), return the original price. (As adjustForTickSize can round down the default price)
  const adjustedPriceIsLowerThanIntstrumentPrice =
    price.lt(new BigNumber(nonAdjustedPrice)) && operation === 'subtraction';
  if (tickSizesForInstrument.length === 0 || (adjustedPriceIsLowerThanIntstrumentPrice && numberOfShifts === 1)) {
    return price.toNumber();
  }

  let i: number = adjustedPriceIsLowerThanIntstrumentPrice ? 1 : 0; // handle multiple subtraction and already tickSize adjusted price
  let resultPrice: BigNumber = price;
  for (; i < numberOfShifts; i++) {
    resultPrice = handleTickSizeOperation(resultPrice, tickSizesForInstrument, getInstrumentTickSizeOfList, operation);
  }

  return resultPrice.toNumber();
};

const handleTickSizeOperation = (
  price: BigNumber,
  tickSizesForInstrument: Array<TickSize>,
  getInstrumentTickSizeOfList: (tickSizes: Array<TickSize>, price: number) => number,
  operation: HandleTickSizeOperation,
) => {
  if (operation === 'addition') {
    const currentTickSize = new BigNumber(getInstrumentTickSizeOfList(tickSizesForInstrument, price.toNumber()));
    return price.plus(currentTickSize);
  } else {
    // Check if the current price is at a lower limit of a tickSize interval (=== upper limit for the level one below)
    const instrumentPriceAsLowerLimitIndex = tickSizesForInstrument.findIndex(tickSize =>
      price.eq(new BigNumber(tickSize.lowerLimitPrice)),
    );

    // If the current price is a lower limit of a tick size interval, then we should only subtract the tickSize one level below
    const currentTickSize = new BigNumber(
      instrumentPriceAsLowerLimitIndex !== -1 && instrumentPriceAsLowerLimitIndex !== 0
        ? tickSizesForInstrument[instrumentPriceAsLowerLimitIndex - 1].tickSizeValue
        : getInstrumentTickSizeOfList(tickSizesForInstrument, price.toNumber()),
    );

    return price.minus(currentTickSize);
  }
};
